import { Gift, HandCoins, Scale, Ticket } from 'lucide-react';
import { Link } from 'react-router';
import {
  CreateTileMockup,
  EXAMPLE,
  GuessTimeline,
  HeroPhoneMockup,
  RevealTileMockup,
  ShareTileMockup,
  exampleWinner,
  exampleWinnerOffBy,
} from '../components/ProductMockups';
import { formatTotalSeconds, formatTotalSecondsCompact } from '../lib/format';

export function HomePage() {
  return (
    <article className="page-wide home">
      <section className="home-hero">
        <div>
          <h1>Closest-guess sweepstakes for charity walks, runs and rides.</h1>
          <p className="lede">
            Set up a sweepstake in a minute, share a short link, and everyone guesses the finish time.
            Free to run, free to enter, and donations go directly to the fundraiser's page — we don't touch the money.
          </p>
          <p className="home-cta">
            <Link className="btn btn-primary btn-lg" to="/sweepstakes/new">
              Set up a sweepstake
            </Link>
            <a href="/how-it-works">or see how it works first</a>
          </p>
        </div>
        <div className="home-hero-visual">
          <HeroPhoneMockup />
        </div>
      </section>

      <section aria-labelledby="steps-heading">
        <div className="home-section-head">
          <h2 id="steps-heading">The 30-second version</h2>
          <a href="/how-it-works">Full walkthrough →</a>
        </div>
        <ol className="home-steps">
          <li className="home-step">
            <CreateTileMockup />
            <h3><span className="home-step-num" aria-hidden="true">1</span>Create</h3>
            <p>Sign in with a 6-digit code, then tell us who's doing what and where donations go.</p>
          </li>
          <li className="home-step">
            <ShareTileMockup />
            <h3><span className="home-step-num" aria-hidden="true">2</span>Share</h3>
            <p>Share the short link with friends, family, colleagues.</p>
          </li>
          <li className="home-step">
            <RevealTileMockup />
            <h3><span className="home-step-num" aria-hidden="true">3</span>Reveal</h3>
            <p>On event day, enter the actual finish time. We pick the winner.</p>
          </li>
        </ol>
      </section>

      <section className="home-payoff" aria-labelledby="payoff-heading">
        <div>
          <p className="eyebrow">Event day</p>
          <h2 id="payoff-heading">Closest guess wins.</h2>
          <p className="lede">
            Enter the actual finish time and we sort everyone's guesses by how close they were to the real number.
            The result goes up on the sweepstake page for everyone who guessed.
          </p>
          <p>
            <Link className="btn btn-primary" to="/sweepstakes/new">
              Start your sweepstake
            </Link>
          </p>
        </div>
        <figure className="card card-hero home-winner">
          <p className="home-winner-title">
            <span aria-hidden="true">🏆 </span>
            {exampleWinner.name} won
          </p>
          <p className="home-winner-line">
            They guessed {formatTotalSecondsCompact(exampleWinner.seconds)}, just{' '}
            {formatTotalSeconds(exampleWinnerOffBy)} off.
          </p>
          <GuessTimeline />
          <figcaption className="home-winner-note">
            Example sweepstake: {EXAMPLE.title}, in aid of {EXAMPLE.charity}.
          </figcaption>
        </figure>
      </section>

      <section aria-labelledby="trust-heading">
        <h2 id="trust-heading" className="sr-only">Free, fair, and hands-off with the money</h2>
        <ul className="home-trust">
          <li>
            <span className="home-trust-icon"><Gift size={22} strokeWidth={1.75} /></span>
            <strong>Free to run</strong>
            <span>No fees, no commission, no card details.</span>
          </li>
          <li>
            <span className="home-trust-icon"><Ticket size={22} strokeWidth={1.75} /></span>
            <strong>Free to enter</strong>
            <span>No purchase necessary. The donation is only ever a suggestion.</span>
          </li>
          <li>
            <span className="home-trust-icon"><HandCoins size={22} strokeWidth={1.75} /></span>
            <strong>We never touch the money</strong>
            <span>Donations go straight to the fundraiser's own page.</span>
          </li>
          <li>
            <span className="home-trust-icon"><Scale size={22} strokeWidth={1.75} /></span>
            <strong>A free prize draw, not a lottery</strong>
            <span>
              Because entry is free. <a href="/guides/uk-charity-sweepstake-legal">Read the legal guide</a>
            </span>
          </li>
        </ul>
      </section>

      <section className="band home-charity" aria-labelledby="charity-heading">
        <div>
          <p className="eyebrow">For charities</p>
          <h2 id="charity-heading">Running this for a charity?</h2>
          <p>
            We offer a free, charity-branded version of the sweepstake page for the first 25 UK charities
            we work with.
          </p>
        </div>
        <a className="btn" href="/charities">See the founding-partner offer</a>
      </section>
    </article>
  );
}
